import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, MailIcon, MessageCircleIcon, PhoneIcon, Trash2Icon } from 'lucide-react';
import { useAuth, useWorkspace } from '../../contexts/AuthContext';
import { addNote, assignLead, deleteLead, deleteNote, updateLeadStage, updateNote } from '../../services/leadService';
import { answerToText, formatDateTime, fullName } from '../../utils/format';
import { PageHeader } from '../../components/crm/PageHeader';
import { ConfirmDialog } from '../../components/crm/ConfirmDialog';
import { Button } from '../../components/ui/Button';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs';
import { Avatar } from '../../components/ui/Avatar';
export function LeadDetail() {
  const {
    id = ''
  } = useParams();
  const navigate = useNavigate();
  const {
    user,
    members,
    hasPermission
  } = useAuth();
  const {
    leads,
    stages,
    notes,
    history,
    forms,
    landings
  } = useWorkspace();
  const [noteBody, setNoteBody] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingBody, setEditingBody] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const lead = leads.find(l => l.id === id);
  if (!lead) {
    return <div className="rounded-lg border border-border bg-background p-10 text-center" data-id="element-1311" data-mp="33o603gni6czp">
        <p className="text-sm text-muted-foreground" data-id="element-1312" data-mp="2yqwa9zw7bzbc">Este lead ya no existe.</p>
        <Link to="/app/leads" className="mt-4 inline-block" data-id="element-1313" data-mp="3w58lks1bviw2">
          <Button variant="secondary" data-id="element-1314" data-mp="1fgnjx5qt3l">Volver a leads</Button>
        </Link>
      </div>;
  }
  const authorName = user ? fullName(user.firstName, user.lastName) : 'Sistema';
  const leadNotes = notes.filter(n => n.leadId === lead.id);
  const leadHistory = history.filter(h => h.leadId === lead.id).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const stage = stages.find(s => s.id === lead.stageId);
  const canEdit = hasPermission('leads.edit');
  const details: {
    label: string;
    value: string;
  }[] = [{
    label: 'Nombre',
    value: lead.firstName
  }, {
    label: 'Apellidos',
    value: lead.lastName
  }, {
    label: 'Empresa',
    value: lead.company
  }, {
    label: 'Email',
    value: lead.email
  }, {
    label: 'Teléfono',
    value: lead.phone
  }, {
    label: 'Página web',
    value: lead.website || '—'
  }, {
    label: 'Fecha de entrada',
    value: formatDateTime(lead.createdAt)
  }, {
    label: 'Fuente',
    value: lead.origin.source
  }, {
    label: 'Campaña',
    value: lead.origin.campaign || '—'
  }, {
    label: 'Landing',
    value: landings.find(l => l.id === lead.origin.landingId)?.name ?? '—'
  }, {
    label: 'Formulario',
    value: forms.find(f => f.id === lead.origin.formId)?.name ?? '—'
  }, {
    label: 'Último contacto',
    value: lead.lastContactAt ? formatDateTime(lead.lastContactAt) : 'Sin contactar'
  }];
  async function handleAddNote() {
    if (!noteBody.trim() || !lead) return;
    setSavingNote(true);
    await addNote(lead.organizationId, lead.id, user?.id ?? '', authorName, noteBody.trim());
    setNoteBody('');
    setSavingNote(false);
  }
  async function handleDelete() {
    if (!lead) return;
    setDeleting(true);
    await deleteLead(lead.id);
    setDeleting(false);
    navigate('/app/leads');
  }
  return <>
      <Link to="/app/leads" className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-id="element-1315" data-mp="1t2j5l04e1bb">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-1316" data-mp="23m8hgnnowwxf" />
        Volver a leads
      </Link>

      <PageHeader title={fullName(lead.firstName, lead.lastName)} description={lead.company} actions={<>
            <Button variant="secondary" leftIcon={<MessageCircleIcon className="h-4 w-4" data-id="element-1319" data-mp="2122fummjtik1" />} onClick={() => window.open(`https://wa.me/${lead.phone.replace(/\D/g, '')}`, '_blank', 'noopener')} data-id="element-1318" data-mp="213538kknx2v7">
              WhatsApp
            </Button>
            <Button variant="secondary" leftIcon={<MailIcon className="h-4 w-4" data-id="element-1321" data-mp="2f1v2a5r1lcm7" />} onClick={() => {
        window.location.href = `mailto:${lead.email}`;
      }} data-id="element-1320" data-mp="2a3tifilsl99o">
              Email
            </Button>
            <Button variant="secondary" leftIcon={<PhoneIcon className="h-4 w-4" data-id="element-1323" data-mp="1md670nco41id" />} onClick={() => {
        window.location.href = `tel:${lead.phone.replace(/\s/g, '')}`;
      }} data-id="element-1322" data-mp="1i9rgv3v0m4r7">
              Llamar
            </Button>
            {hasPermission('leads.delete') && <Button variant="destructive" leftIcon={<Trash2Icon className="h-4 w-4" data-id="element-1325" data-mp="1w5bzrzaqumym" />} onClick={() => setConfirmDelete(true)} data-id="element-1324" data-mp="1w4anxslwlwfu">
                Eliminar
              </Button>}
          </>} data-id="element-1317" data-mp="1zlolhneurzcv" />

      <div className="grid gap-5 lg:grid-cols-3" data-id="element-1326" data-mp="13m6fbedsgdle">
        {/* Columna principal */}
        <div className="space-y-5 lg:col-span-2" data-id="element-1327" data-mp="w36zfipjd46s">
          <section className="rounded-lg border border-border bg-background p-5 shadow-sm" data-id="element-1328" data-mp="w2npopa1hm3u">
            <h2 className="mb-4 text-sm font-semibold text-foreground" data-id="element-1329" data-mp="r4vkwuoaq5i8">
              Datos principales
            </h2>
            <dl className="grid gap-4 sm:grid-cols-2" data-id="element-1330" data-mp="m7c8m0b5s2sx">
              {details.map(item => <div key={item.label} data-id="element-1331" data-mp="i34gdt5hs4py">
                  <dt className="text-xs uppercase tracking-wide text-muted-foreground" data-id="element-1332" data-mp="d4kvlw2mwyz6">
                    {item.label}
                  </dt>
                  <dd className="mt-1 break-words text-sm text-foreground" data-id="element-1333" data-mp="8d2sp2h1wobk">
                    {item.value}
                  </dd>
                </div>)}
            </dl>
          </section>

          <Tabs defaultValue="respuestas" data-id="element-1334" data-mp="3um42ddf433cf">
            <TabsList data-id="element-1335" data-mp="3uizsbzeytddu">
              <TabsTrigger value="respuestas" data-id="element-1336" data-mp="3qj7qs6nzo7hz">Respuestas</TabsTrigger>
              <TabsTrigger value="notas" data-id="element-1337" data-mp="3qlk4u8qmlxpr">Notas</TabsTrigger>
              <TabsTrigger value="historial" data-id="element-1338" data-mp="3llfmsea2kuxf">Historial</TabsTrigger>
            </TabsList>

            <TabsContent value="respuestas" data-id="element-1339" data-mp="3bwiqw3ptrsb2">
              {lead.answers.length === 0 ? <p className="rounded-lg border border-dashed border-border bg-surface p-8 text-center text-sm text-muted-foreground" data-id="element-1340" data-mp="3fy41fievzui3">
                  Este lead no tiene respuestas asociadas.
                </p> : <dl className="divide-y divide-border rounded-lg border border-border bg-background" data-id="element-1341" data-mp="36xflgi5xnk2k">
                  {lead.answers.map(answer => <div key={answer.questionId} className="p-4" data-id="element-1342" data-mp="3uobbb9qtw576">
                      <dt className="text-sm font-medium text-foreground" data-id="element-1343" data-mp="3gpcpviyusx6">
                        {answer.label}
                      </dt>
                      <dd className="mt-1 text-sm text-muted-foreground" data-id="element-1344" data-mp="3kwnq3qkv7ke5">
                        {answerToText(answer.value) || '—'}
                      </dd>
                    </div>)}
                </dl>}
            </TabsContent>

            <TabsContent value="notas" data-id="element-1345" data-mp="hend1x0gcoto">
              {hasPermission('notes.edit') && <div className="mb-4 rounded-lg border border-border bg-background p-4" data-id="element-1346" data-mp="m91by9ebpe33">
                  <Textarea id="nueva-nota" label="Nueva nota" rows={3} placeholder="Escribe el resultado de la llamada, próximos pasos…" value={noteBody} onChange={e => setNoteBody(e.target.value)} data-id="element-1347" data-mp="26wkkqv787hjh" />
                  <div className="mt-3 flex justify-end" data-id="element-1348" data-mp="2fovybgh8rai1">
                    <Button size="sm" loading={savingNote} disabled={!noteBody.trim()} onClick={handleAddNote} data-id="element-1349" data-mp="2bou6dz1b5vfy">
                      Añadir nota
                    </Button>
                  </div>
                </div>}
              {leadNotes.length === 0 ? <p className="rounded-lg border border-dashed border-border bg-surface p-8 text-center text-sm text-muted-foreground" data-id="element-1350" data-mp="1j180gk4lyepx">
                  Todavía no hay notas para este lead.
                </p> : <ul className="space-y-3" data-id="element-1351" data-mp="1rztjegdzrvr8">
                  {leadNotes.map(note => <li key={note.id} className="rounded-lg border border-border bg-background p-4" data-id="element-1352" data-mp="38ebjgz2mejro">
                      <div className="flex items-center gap-3" data-id="element-1353" data-mp="33g9qnmfe7630">
                        <Avatar name={note.authorName} size="sm" data-id="element-1354" data-mp="33i4bl0qnyy73" />
                        <div className="min-w-0 flex-1" data-id="element-1355" data-mp="3njfoe8krq7n2">
                          <p className="text-sm font-medium text-foreground" data-id="element-1356" data-mp="3sgpjo4uu9dit">
                            {note.authorName}
                          </p>
                          <p className="text-xs text-muted-foreground" data-id="element-1357" data-mp="bvjag8hmih9">
                            {formatDateTime(note.createdAt)}
                          </p>
                        </div>
                      </div>
                      {editingId === note.id ? <div className="mt-3" data-id="element-1358" data-mp="f1t2ehrk8hzl">
                          <Textarea id={`edit-${note.id}`} rows={3} value={editingBody} onChange={e => setEditingBody(e.target.value)} data-id="element-1359" data-mp="f0hymhy47a8h" />
                          <div className="mt-2 flex justify-end gap-2" data-id="element-1360" data-mp="o2r2wq2si7df">
                            <Button size="sm" variant="ghost" onClick={() => setEditingId(null)} data-id="element-1361" data-mp="o0n3olfu5wjh">
                              Cancelar
                            </Button>
                            <Button size="sm" onClick={async () => {
                      await updateNote(note.id, editingBody);
                      setEditingId(null);
                    }} data-id="element-1362" data-mp="2puvuu1kah4o5">
                              Guardar
                            </Button>
                          </div>
                        </div> : <>
                          <p className="mt-3 text-sm leading-relaxed text-foreground" data-id="element-1363" data-mp="28iffomd4rnyh">
                            {note.body}
                          </p>
                          {note.authorId === user?.id && hasPermission('notes.edit') && <div className="mt-3 flex gap-2" data-id="element-1364" data-mp="2wam8xcnk7eto">
                              <Button size="sm" variant="ghost" onClick={() => {
                      setEditingId(note.id);
                      setEditingBody(note.body);
                    }} data-id="element-1365" data-mp="2wb5tl14lghbe">
                                Editar
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => deleteNote(note.id)} data-id="element-1366" data-mp="1gudnnedzx655">
                                Eliminar
                              </Button>
                            </div>}
                        </>}
                    </li>)}
                </ul>}
            </TabsContent>

            <TabsContent value="historial" data-id="element-1367" data-mp="2iolmfyhzuqp9">
              <ol className="space-y-3" data-id="element-1368" data-mp="2we6voh5zrrk3">
                {leadHistory.map(event => <li key={event.id} className="flex gap-3 rounded-lg border border-border bg-background p-4" data-id="element-1369" data-mp="2wgjmzkxn3e9t">
                    <span className="mt-1.5 h-2 w-2 flex-none rounded-full bg-primary" data-id="element-1370" data-mp="2rlvw5ff5y7s2" />
                    <div data-id="element-1371" data-mp="36epo40bmggdm">
                      <p className="text-sm text-foreground" data-id="element-1372" data-mp="31ds6ppyfrqd6">{event.description}</p>
                      <p className="mt-1 text-xs text-muted-foreground" data-id="element-1373" data-mp="31fw6bvpay37n">
                        {event.authorName} · {formatDateTime(event.createdAt)}
                      </p>
                    </div>
                  </li>)}
              </ol>
            </TabsContent>
          </Tabs>
        </div>

        {/* Columna lateral */}
        <aside className="space-y-5" data-id="element-1374" data-mp="1gy6naasjwo6r">
          <section className="rounded-lg border border-border bg-background p-5 shadow-sm" data-id="element-1375" data-mp="1gz9g4o9fp0pb">
            <h2 className="mb-4 text-sm font-semibold text-foreground" data-id="element-1376" data-mp="16u9u1bfo2jnb">Estado</h2>
            <div className="mb-4" data-id="element-1377" data-mp="1aua8gw2rmuud">
              <StatusBadge status={(stage?.name ?? 'Nuevo') as never} data-id="element-1378" data-mp="1axfwe7znrqoi" />
            </div>
            <Select id="estado" label="Cambiar estado" disabled={!canEdit} options={stages.map(s => ({
            value: s.id,
            label: s.name
          }))} value={lead.stageId} onChange={e => updateLeadStage(lead.id, e.target.value, authorName)} data-id="element-1379" data-mp="x3olav6a3yqo" />
            <div className="mt-4" data-id="element-1380" data-mp="s53wdrwh91hj">
              <Select id="comercial" label="Comercial asignado" placeholder="Sin asignar" disabled={!canEdit} options={members.map(m => ({
              value: m.userId,
              label: fullName(m.firstName, m.lastName)
            }))} value={lead.assignedTo ?? ''} onChange={e => {
              const member = members.find(m => m.userId === e.target.value);
              assignLead(lead.id, e.target.value || undefined, member ? fullName(member.firstName, member.lastName) : '', authorName);
            }} data-id="element-1381" data-mp="s658lzp8bu0u" />
            </div>
          </section>

          <section className="rounded-lg border border-border bg-background p-5 shadow-sm" data-id="element-1382" data-mp="271ewlzej9s4e">
            <h2 className="mb-4 text-sm font-semibold text-foreground" data-id="element-1383" data-mp="222bqscqqbhvd">
              Origen del lead
            </h2>
            <dl className="space-y-3 text-sm" data-id="element-1384" data-mp="1x4sdpg5rpb50">
              {[['source', lead.origin.source], ['medium', lead.origin.medium], ['campaign', lead.origin.campaign || '—'], ['content', lead.origin.content || '—'], ['term', lead.origin.term || '—'], ['referrer', lead.origin.referrer || 'directo']].map(([label, value]) => <div key={label} className="flex justify-between gap-3" data-id="element-1385" data-mp="2l0o327cxt7v6">
                  <dt className="text-muted-foreground" data-id="element-1386" data-mp="2g34n930lcn14">{label}</dt>
                  <dd className="truncate text-foreground" data-id="element-1387" data-mp="2g0s8eyq4qssa">{value}</dd>
                </div>)}
            </dl>
          </section>
        </aside>
      </div>

      <ConfirmDialog open={confirmDelete} title="Eliminar este lead" description="Se eliminarán también sus notas y su historial. Esta acción no se puede deshacer." loading={deleting} onCancel={() => setConfirmDelete(false)} onConfirm={handleDelete} data-id="element-1388" data-mp="38q8vdguzdhh4" />
    </>;
}